import {
  CustomTooltip,
  StyledReactSelect,
  styledSelectDefaultStyles,
} from '@fountain/fountain-ui-components';
import { Checkbox, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';
import { components as reactSelectComponents } from 'react-select';
import type { StylesConfig } from 'react-select/src/styles';

import {
  buildRows,
  GroupRow,
  groupSelectionCount,
  MultiSelectGroup,
  MultiSelectRow,
  OptionRow,
  rowMatches,
  toggleGroupMembers,
  visibleMembers,
} from './groupedOptions';
import messages from './messages';
import { useStyles } from './styles';

/**
 * One entry in a set-membership picker.
 *
 * Deliberately not `ConditionSelectOption`, which also carries `isDisabled` for
 * the condition-type picker. Nothing here disables a row, and borrowing a type
 * with a field this control drops would let a caller set one that silently does
 * nothing.
 */
export interface SearchableMultiSelectOption {
  value: string;
  /** Falls back to the value — a funnel's title is nullable. */
  label?: string;
  /**
   * The `external_id` of the group this option sits under, joining to a
   * `groups` entry's `value`. Read only when `groups` is passed.
   */
  group?: string | null;
}

export interface SearchableMultiSelectProps {
  /** Lands on the input, so the row's controls stay individually addressable. */
  id: string;
  label: string;
  /**
   * Draws no label above the control, for a caller that draws the caption
   * itself because another control shares its row. `label` still names the
   * control to assistive technology.
   */
  hideLabel?: boolean;
  /** What the field holds, in the order it holds it. */
  values: string[];
  options: SearchableMultiSelectOption[];
  onChange: (values: string[]) => void;
  /**
   * The headers to file the options under, in the order they are shown.
   *
   * Passing this — even empty — puts the menu in grouped mode. Only the
   * locations condition is grouped; every other caller has no grouping to show,
   * and a header row an admin cannot use is a row in the way.
   */
  groups?: SearchableMultiSelectOption[];
  /** The message for this control, once the admin has tried to save. */
  error?: string;
  /**
   * Said when the server's cap cut the option list short, so a search over a
   * partial list is never read as a search over the account.
   */
  note?: string;
  /** The control's place in the row, which the row decides rather than this. */
  className?: string;
  /**
   * Whether the control shares its line with other controls.
   *
   * Only a condition row does. It decides the chip height and nothing else — see
   * `DENSE_SELECT_STYLES`.
   */
  dense?: boolean;
}

interface MultiSelectOptionProps {
  data: MultiSelectRow;
  innerProps: React.HTMLAttributes<HTMLDivElement>;
  innerRef: React.Ref<HTMLDivElement>;
  isFocused: boolean;
  isSelected: boolean;
  /**
   * react-select hands its own props down. Two are read: what the field holds,
   * and what the admin has typed — a header describes the part of its group that
   * survived the search, so it cannot be rendered without both.
   */
  selectProps: { value?: OptionRow[] | null; inputValue?: string };
}

/**
 * One row of the menu — an option, or a header standing for the options under
 * it.
 *
 * A checkbox rather than the design system's own right-hand check mark, on both
 * kinds of row. The header has three states and the design system's mark has
 * two, so a header cannot borrow it; and a menu that marked a header one way and
 * its members another would be showing one thing in two shapes. The checkbox is
 * the design system's own `Checkbox`, styled by the FDS theme — 16px box, brand
 * fill, white check, dash for a partial group — rather than an icon drawn here.
 *
 * It is inert: `aria-hidden` and out of the tab order. The row is the control,
 * and react-select drives it by click and by Enter on the focused row. A real
 * checkbox left in the accessibility tree would announce a second control per
 * row and put a tab stop inside a menu that has none.
 */
const MultiSelectOption: React.FC<MultiSelectOptionProps> = ({
  data,
  innerProps,
  innerRef,
  isFocused,
  isSelected,
  selectProps,
}) => {
  const styles = useStyles();

  if (data.kind === 'group') {
    const held = new Set((selectProps.value ?? []).map(row => row.value));
    const shown = visibleMembers(data, selectProps.inputValue ?? '');
    const chosen = groupSelectionCount(shown, held);
    // Checked means the whole group, so the three states map onto the design
    // system's checkbox as they are: box, dash, check.
    const all = shown.length > 0 && chosen === shown.length;

    return (
      <Grid
        {...innerProps}
        ref={innerRef}
        alignItems="center"
        className={clsx(styles.conditionGroupRow, { focused: isFocused })}
        container
        wrap="nowrap"
      >
        <Checkbox
          checked={all}
          className={styles.conditionOptionCheckbox}
          color="primary"
          disableRipple
          indeterminate={chosen > 0 && !all}
          inputProps={{ 'aria-hidden': true, readOnly: true, tabIndex: -1 }}
        />
        <Typography
          className={clsx(styles.conditionOptionLabel, 'group')}
          color="textPrimary"
          title={data.label}
          variant="body2"
        >
          {data.label}
        </Typography>
        {/* The count of what the header stands for right now, which is the set
            its checkbox describes and the set clicking it toggles. Under a
            search that is a subset of the group, so it is read off what is on
            screen rather than off the account's membership. */}
        <Typography className={styles.conditionGroupCount} variant="body2">
          {shown.length}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      {...innerProps}
      ref={innerRef}
      alignItems="center"
      className={clsx(styles.conditionOptionRow, {
        focused: isFocused,
        indented: data.indented,
        selected: isSelected,
      })}
      container
      wrap="nowrap"
    >
      <Checkbox
        checked={isSelected}
        className={styles.conditionOptionCheckbox}
        color="primary"
        disableRipple
        inputProps={{ 'aria-hidden': true, readOnly: true, tabIndex: -1 }}
      />
      {/* `title` for the reason the design system's own option row carries one:
          an account names its own locations and its own people, and the control
          is narrow enough that a long name is read from the tooltip. */}
      <Typography
        className={styles.conditionOptionLabel}
        color="textPrimary"
        title={data.label}
        variant="body2"
      >
        {data.label}
      </Typography>
    </Grid>
  );
};

interface ChipLabelProps {
  data: MultiSelectRow;
  innerProps: React.HTMLAttributes<HTMLDivElement>;
  selectProps: {
    getOptionLabel?: (row: MultiSelectRow) => string;
    /** react-select hands its whole prop set down; only this is read. */
    menuIsOpen?: boolean;
  };
}

/**
 * A chip, with the whole of a name it is cutting available on hover (AXHE-5140).
 *
 * The chip carries the ellipsis, so on a location row — three controls inside a
 * level card — an account's own place names reach it routinely, and no width
 * this picker can be given holds a name an account is free to make arbitrarily
 * long.
 *
 * Hover only. The row's other pickers answer keyboard focus as well, because
 * focus lands on the control and the control shows one value; a chip is one of
 * several inside a control and has no tab stop of its own — react-select's own
 * remove is deliberately not one — so there is nothing for focus to name here.
 * What a screen reader hears is unaffected either way: react-select builds its
 * live region from `getOptionLabel`, which is the whole name already.
 */
const ChipLabel: React.FC<ChipLabelProps> = props => {
  const { data, selectProps } = props;
  const labelRef = React.useRef<HTMLDivElement | null>(null);
  const [showingFullName, setShowingFullName] = React.useState(false);

  const revealIfCut = () => {
    const box = labelRef.current;
    setShowingFullName(box !== null && box.scrollWidth > box.clientWidth);
  };

  return (
    <CustomTooltip
      dense
      onClose={() => setShowingFullName(false)}
      onOpen={revealIfCut}
      // Silent while the list is open, for the reason `ConditionSelect` is: the
      // tooltip and the menu are two boxes over the same control, and the menu
      // is the one the admin opened. Read straight off `selectProps`, which
      // carries react-select's own props to every component it renders — this
      // one sits inside the select, where `ConditionSelect`'s tooltip sits
      // outside it and has to watch the menu mount instead.
      open={showingFullName && !selectProps.menuIsOpen}
      placement="top"
      title={selectProps.getOptionLabel?.(data) ?? data.label}
    >
      <reactSelectComponents.MultiValueLabel
        {...props}
        // react-select spreads `innerProps` straight onto the div it draws, so
        // this is the one hook it offers onto the element that does the cutting.
        innerProps={{ ...props.innerProps, ref: labelRef }}
      />
    </CustomTooltip>
  );
};

/**
 * Stable across renders, because react-select rebuilds its component map from
 * this object on every one: a type minted inside the render would unmount the
 * menu and drop the focused row on each keystroke.
 */
const SELECT_COMPONENTS = {
  MultiValueLabel: ChipLabel,
  Option: MultiSelectOption,
} as unknown as Record<string, React.ComponentType<unknown>>;

/**
 * Room for the span `CustomTooltip` puts between the chip and its label.
 *
 * The label is what carries `overflow: hidden` and the ellipsis, and it is now a
 * grandchild of the chip rather than a child. A flex item's automatic minimum is
 * its own content, so without this the span refuses to shrink, the label never
 * reaches its own overflow, and a long name pushes the chip past the control
 * instead of ending in an ellipsis inside it.
 */
const CHIP_LABEL_WRAPPER = { '& > span': { display: 'flex', minWidth: 0 } };

/**
 * The menu sizes to its own content rather than to the control.
 *
 * It takes the control's width by default, and on a condition row that is about
 * 180px — narrower than the names inside it. An account names its own location
 * groups, and a long one was truncated in the menu a reader opens precisely to
 * read those names.
 *
 * Sized on the portal wrapper rather than on the menu, because that is the
 * element react-select positions and the only one that knows where the control
 * is. A cap written on the menu bounds how wide it gets and says nothing about
 * where its right edge lands: the wrapper is placed at the control's left and
 * portalled to `document.body`, so nothing downstream clips it, and a 320px
 * menu opened from the second entity picker of a location row ended 59px past
 * the right edge of a 1512px window. Measured against the room actually left to
 * the right of the control instead, so no group name can carry it off-screen.
 *
 * The menu itself keeps the design system's rule, which is already `width:
 * 100%` of this wrapper.
 *
 * Composed onto `styledSelectDefaultStyles` rather than replacing it: a caller's
 * `styles` prop lands after `StyledSelect`'s own and takes the whole object, so
 * an override naming one key would drop the control, chip and indicator styling
 * with it. `ActionsCard` in the workflow editor spreads it the same way.
 */
const SELECT_STYLES: StylesConfig = {
  // Cast only over the spread. The design system's own `container` returns
  // `null` for a select that is not an inline search, which react-select
  // tolerates and its types do not. Typing the object is still worth it: the
  // keys written below are checked against react-select's own names, which is
  // the one mistake this shape could otherwise not catch.
  ...(styledSelectDefaultStyles as StylesConfig),
  multiValue: provided => ({
    ...styledSelectDefaultStyles.multiValue(provided),
    ...CHIP_LABEL_WRAPPER,
  }),
  menuPortal: (provided, state: { rect: { left: number; width: number } }) => ({
    // One argument: the design system's own `menuPortal` takes only the base,
    // and adds the z-index that puts this above the drawer.
    ...styledSelectDefaultStyles.menuPortal(provided),
    width: 'max-content',
    minWidth: state.rect.width,
    maxWidth: `calc(100vw - ${Math.round(state.rect.left)}px - 16px)`,
  }),
};

/**
 * The same, plus the design system's chip at the density a condition row needs.
 *
 * The chip's own padding is `8px 14px 8px 10px`, which makes it 32px tall. That
 * is right wherever this picker has its line to itself, and it is what a
 * location row cannot afford: three controls share the row's 518px there, so
 * each picker renders about 180px wide and two 32px chips stack instead of
 * sitting side by side. The row is then 102px tall for two place names.
 *
 * Only the padding and the gap come down. The fill, the text color and the
 * radius stay the design system's — and so does the whole chip everywhere the
 * width is not the problem. A second look for one component inside one product
 * is the inconsistency this exists to avoid, and shrinking the approver rosters,
 * which have the room, is exactly how it would be introduced.
 */
const DENSE_SELECT_STYLES: StylesConfig = {
  ...SELECT_STYLES,
  multiValue: provided => ({
    ...styledSelectDefaultStyles.multiValue(provided),
    ...CHIP_LABEL_WRAPPER,
    gap: 4,
    padding: '4px 6px 4px 8px',
  }),
};

/**
 * A picker that holds a set — the condition rows' positions, openings and
 * locations, and a level's approver rosters.
 *
 * Filter-as-you-type, chips in the control, a per-chip remove and a clear-all,
 * which is what Design Requirements §3 and §4 both ask of a picker over a list
 * an account can grow to hundreds of rows.
 *
 * The multi sibling of `ConditionSelect`, and configured the same way for the
 * same reasons: `size="small"` to match the controls beside it, no `required`
 * asterisk on a surface where every control is, an empty placeholder because the
 * label already names the box — a placeholder is never a label. Read that
 * component's notes for those three.
 *
 * What is specific to holding a set:
 *
 * - `isClearable`, which is the design's clear-all. react-select turns it on for
 *   a multi by default; it is stated here because it is a requirement rather
 *   than an inheritance.
 * - `hideSelectedOptions={false}`. react-select drops a chosen option out of the
 *   menu for a multi, so the list shortens as you pick and the same row cannot be
 *   used to unpick. These lists are an account's own positions, locations and
 *   people, and a list that rearranges itself under the pointer is the worse
 *   trade.
 * - A stored value the option list does not carry still becomes a chip. The
 *   server caps each list, openings are active ones only and a user roster drops
 *   offboarded people, so a rule scoped to an opening that has since closed —
 *   or staffed by someone since offboarded — would otherwise lose that row the
 *   moment anyone opened the rule, silently widening a compliance gate on save.
 *
 * With `groups`, the menu also files its options under group headers. Grouping
 * takes the search over from react-select, because a header stands for the part
 * of its group the search left on screen: the count it shows, the state its
 * checkbox reads and the set it toggles all have to read that one list. It also
 * needs a matcher of its own — react-select's would run over a header's text,
 * which is a group name rather than an entity's, and drop every header.
 *
 * The row list itself is built from the options alone and kept across
 * keystrokes. react-select tracks the focused row by object identity, so a list
 * rebuilt per character would send the keyboard back to the top of the menu on
 * every one.
 *
 * A header selects its members individually. It never stores the group: a
 * group-valued condition is its own scope, chosen from its own picker, and it
 * keeps matching locations added to the group later — which a set of names
 * cannot do.
 *
 * Both messages are wired to the input with `aria-describedby` rather than
 * rendered beside it: the design system passes that attribute through to the
 * real input (AXHE-4666), so the note and the error are announced with the
 * control instead of being read only by someone who happens to move past them.
 */
export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  id,
  label,
  hideLabel = false,
  values,
  options,
  onChange,
  groups,
  error,
  note,
  className,
  dense,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const grouped = groups !== undefined;

  /**
   * What the admin has typed, held here rather than by react-select.
   *
   * Only grouped mode reads it. Flat mode leaves the search where react-select
   * keeps it, so the two pickers beside this one behave exactly as they did.
   */
  const [search, setSearch] = React.useState('');

  // Memoised on the caller's array for the reason `ConditionSelect` gives:
  // react-select takes a `setState` whenever `options` changes by identity, and
  // the panel re-renders on every keystroke elsewhere in the row.
  const resolved = React.useMemo(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label ?? option.value,
        group: option.group ?? null,
      })),
    [options],
  );

  const headers = React.useMemo(
    () =>
      (groups ?? []).map(
        (group): MultiSelectGroup => ({
          value: group.value,
          label: group.label ?? group.value,
        }),
      ),
    [groups],
  );

  /**
   * Built from the options alone, never from the search. react-select tracks the
   * focused row by object identity, so rows rebuilt on a keystroke would send
   * the keyboard back to the top of the menu mid-search — `buildRows` carries
   * the mechanism. `rowMatches` hides what the search excludes instead.
   */
  const rows = React.useMemo(
    (): MultiSelectRow[] =>
      grouped
        ? buildRows(resolved, headers)
        : resolved.map(option => ({
            kind: 'option',
            value: option.value,
            label: option.label,
            indented: false,
          })),
    [grouped, headers, resolved],
  );

  /**
   * One chip per distinct value, in the order the field holds them.
   *
   * The de-duplication is not cosmetic. react-select identifies a value by its
   * `value`, so `removeValue` filters *every* entry matching the one clicked — a
   * field storing the same position twice would render two chips and lose both
   * to one X. Both callers hold a set — a membership test, a roster — so the
   * second copy was never meaning anything; showing one chip is what the rule
   * actually does, and it makes the X remove exactly what it appears to.
   *
   * Nothing in this panel can write a duplicate, so this only ever reads one
   * that arrived another way — an older rule, an API write, an import.
   */
  const selected = React.useMemo(
    () =>
      Array.from(new Set(values)).map((value): OptionRow => {
        const match = resolved.find(option => option.value === value);

        return {
          kind: 'option',
          value,
          // The stored id or name, which is a worse label than the record's own
          // and still says which record. `id_options` ships a null label for a
          // funnel with no title, so the catalog does this too.
          label: match?.label ?? value,
          indented: false,
        };
      }),
    [resolved, values],
  );

  const held = React.useMemo(
    () => new Set(selected.map(row => row.value)),
    [selected],
  );

  /**
   * What react-select announces when a row takes focus, and what names a chip.
   *
   * A header's own text is a name, a count and a checkbox — three things a
   * screen reader reads as one word unless the name says the rest. react-select
   * v3 builds its live region from this call rather than from the row's markup,
   * so the state has to be in the string.
   */
  const nameFor = (row: MultiSelectRow) => {
    if (row.kind !== 'group') return row.label;

    const shown = visibleMembers(row, search);

    return intl.formatMessage(messages.conditionGroupOption, {
      count: shown.length,
      label: row.label,
      selected: groupSelectionCount(shown, held),
    });
  };

  /**
   * A header is spent rather than stored: it arrives as the option that was
   * acted on, and what it leaves behind is a change to its members.
   *
   * Named by react-select's own action metadata, because the header never
   * reaches the value array — `isOptionSelected` reports it already chosen, so
   * react-select takes it down its deselect path and hands back the value
   * untouched. That is what keeps a header out of the design system's "is
   * everything picked" count, which would otherwise shut the menu the moment a
   * header click completed the only group on the account.
   *
   * The incoming list is read as well, so the header is still found if a later
   * react-select stops reporting the option or starts appending it.
   */
  const handleChange = (
    next?: MultiSelectRow[] | null,
    meta?: { option?: MultiSelectRow },
  ) => {
    const chosen = next ?? [];
    const acted = meta?.option;
    const header =
      (acted && acted.kind === 'group' ? acted : undefined) ??
      chosen.find((row): row is GroupRow => row.kind === 'group');

    if (header) {
      onChange(
        toggleGroupMembers(
          selected.map(row => row.value),
          visibleMembers(header, search).map(member => member.value),
        ),
      );
      return;
    }

    onChange(chosen.map(row => row.value));
  };

  const errorId = `${id}-error`;
  const noteId = `${id}-note`;
  const describedBy = [error && errorId, note && noteId]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={clsx(styles.conditionSelect, className)}>
      <StyledReactSelect
        inputId={id}
        label={hideLabel ? undefined : label}
        // Both, for the reason `ConditionSelect` passes both: the design system
        // names the control from the rendered label only when the caller names
        // nothing itself, and several selects in this app pass `label` alone and
        // are unnamed to a screen reader.
        aria-label={label}
        aria-describedby={describedBy || undefined}
        options={rows}
        value={selected}
        onChange={handleChange}
        components={SELECT_COMPONENTS}
        styles={dense ? DENSE_SELECT_STYLES : SELECT_STYLES}
        getOptionLabel={nameFor}
        // Answered from the value react-select passes in, never from `held`
        // above. react-select rebuilds the menu inside
        // `UNSAFE_componentWillReceiveProps`, where `this.props` is still the
        // render before — so a closure over the component's own state answers
        // every row against the selection as it was *before* the click that
        // changed it, and the rows a click just chose stay unchecked until
        // something else reopens the menu. The argument is the new selection.
        //
        // A header reports as already chosen so that react-select removes rather
        // than appends it, and removing a row the value never held is a no-op —
        // which is the point. It keeps the header out of the value entirely, and
        // out of the count the design system closes the menu on.
        isOptionSelected={(row: MultiSelectRow, chosen: OptionRow[] = []) =>
          row.kind === 'group' ||
          chosen.some(picked => picked.value === row.value)
        }
        {...(grouped
          ? {
              inputValue: search,
              onInputChange: (value: string) => setSearch(value),
              // The search runs here rather than inside the row list, which is
              // built once and kept — see `buildRows`. It replaces react-select's
              // own matching, which would run over a header's text and drop every
              // one of them.
              filterOption: (option: {
                label: string;
                value: number | string;
                data?: MultiSelectRow;
              }) => (option.data ? rowMatches(option.data, search) : true),
            }
          : {})}
        // Boolean, not the message: a string `error` renders the design system's
        // own caption 24px below the control, which on a row this dense lands on
        // the control underneath. The border still turns, and the message is
        // rendered in flow below.
        error={Boolean(error)}
        isClearable
        isMulti
        isSearchable
        hideSelectedOptions={false}
        tabSelectsValue={false}
        placeholder=""
        size="small"
      />

      {note && (
        <Typography
          className={styles.conditionSelectNote}
          id={noteId}
          variant="body2"
        >
          {note}
        </Typography>
      )}

      {/* `role="alert"` for the reason `ConditionSelect`'s message carries it:
          this is mounted only once a save has been refused, so its appearance is
          the event to announce, and a refused save moves no focus. */}
      {error && (
        <Typography
          className={styles.conditionSelectError}
          color="error"
          id={errorId}
          role="alert"
          variant="body2"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
